import { createContext, type ReactNode, useContext, useRef } from 'react'
import { type StoreApi, useStore } from 'zustand'

import { createFiltersStore } from './store'
import type { FiltersStore } from './types'

const FiltersContext = createContext<StoreApi<FiltersStore> | null>(null)

interface FiltersProviderProps {
  children: ReactNode
  contextId: string
  withSearchParams?: boolean
}

export const FiltersProvider = ({
  children,
  contextId,
  withSearchParams = false,
}: FiltersProviderProps) => {
  const storeRef = useRef<StoreApi<FiltersStore>>(
    createFiltersStore(contextId, withSearchParams),
  )

  return (
    <FiltersContext.Provider value={storeRef.current}>
      {children}
    </FiltersContext.Provider>
  )
}

export const useFiltersStore = <T,>(
  selector: (state: FiltersStore) => T,
): T => {
  const store = useContext(FiltersContext)

  if (!store) {
    throw new Error('useFiltersStore must be used within FiltersProvider')
  }

  return useStore(store, selector)
}

export const useFilters = () =>
  useFiltersStore((state: FiltersStore) => state.filters)

export const useSearchQuery = () =>
  useFiltersStore((state: FiltersStore) => state.searchQuery)

export const useFiltersActions = () =>
  useFiltersStore((state: FiltersStore) => state.actions)
